import { mapActions } from 'vuex'
import { EventBus, Events } from '../eventBus'

export default {
	computed: {
		/**
		 * @returns {string[]} Array of error messages associated with the contract
		 */
		errorMessages() {
			// TODO: Get error messages from the contract
			return []
		},

		/**
		 * @returns {boolean} Whether this is the consignor's first time
		 */
		firstTimeConsignor() {
			return this.contract?.first_time_consignor || false
		},

		/**
		 * @returns {boolean} Whether there is at least one inventory list attached to the contract
		 */
		hasInventoryList() {
			return this.contract?.inventory_lists?.length > 0 || false
		},

		/**
		 * @returns {boolean} Whether there are any unsigned inventory lists where staff has signed
		 */
		hasUnsignedInventoryList() {
			return (
				this.contract?.inventory_lists?.some(
					(list) =>
						(list.customer_signature === null || list.customer_signature === '') &&
						list.signed_by_name &&
						list.signed_by_name.length > 0
				) || false
			)
		},

		/**
		 * @returns {number[]} Array of inventory list IDs associated with the contract
		 */
		inventoryListIds() {
			return this.contract?.inventory_lists?.map((list) => list.id) || []
		},

		/**
		 * @returns {Object|null} Returns the most recent unsigned inventory list or null if none exists
		 */
		currentEditableList() {
			if (!this.contract?.inventory_lists?.length) return null
			return (
				this.contract.inventory_lists.find(
					(list) => !list.customer_signature || list.customer_signature.length === 0
				) || null
			)
		},

		/**
		 * @returns {boolean} Whether the user is authenticated
		 */
		isAuthenticated() {
			return $cookies.get('llatoken') !== null && $cookies.get('fp_identity') !== null
		},

		/**
		 * @returns {boolean} Whether the user is a designated person
		 */
		isDesignatedPerson() {
			return this.contract?.is_designated_person
		},

		/**
		 * @returns {boolean} Whether the user is staff
		 */
		isStaff() {
			return this.contract?.is_staff
		},

		/**
		 * @returns {boolean} Whether the signing link has been sent to the customer
		 */
		linkSent() {
			// TODO: Will be true after the link is sent to the customer
			return false
		},

		/**
		 * @returns {boolean} Whether the inventory lists have been signed by the customer
		 */
		listSignedByCustomer() {
			return (
				this.contract?.inventory_lists?.every((list) => list.customer_signature?.length > 0) ||
				false
			)
		},

		/**
		 * @returns {boolean} Whether all inventory lists have been signed by staff
		 */
		listSignedByStaff() {
			return (
				this.contract?.inventory_lists?.every((list) => list.signed_by_name?.length > 0) || false
			)
		},

		/**
		 * @returns {string} URL for the logo image
		 */
		logoUrl() {
			return 'https://04e4a73cc9b69a6ee9be-27c3a9c17ee233673ee63c906aa4c75c.ssl.cf1.rackcdn.com/images/podium.jpg'
		},

		/**
		 * @returns {string} Original "heard about" value or current "heard about" value
		 */
		originalHeardAbout() {
			return this.contract?.original_heard_about || this.contract?.heard_about
		},

		/**
		 * @returns {boolean} Whether the contract has been sent to the customer via email
		 */
		sentToCustomer() {
			return this.contract?.sent_customer_email_unixtime?.toString().length > 0 || false
		},

		/**
		 * @returns {boolean} Whether the customer has signed the contract
		 */
		signedByCustomer() {
			return this.contract?.customer_signature_date?.length > 0 || false
		},

		/**
		 * @returns {boolean} Whether the contract has been sent or signed
		 */
		sentOrSigned() {
			return this.sentToCustomer || this.signedByCustomer
		},

		/**
		 * @returns {boolean} Whether staff has signed the contract
		 */
		signedByStaff() {
			return this.contract?.signed_date?.length > 0 || false
		},

		/**
		 * @returns {boolean} Whether the signing process was successful
		 */
		signingSuccess() {
			return this.contract?.signing_success || false
		}
	},
	methods: {
		...mapActions(['updateContract', 'getContract']),

		/**
		 * @param {boolean} [bypass=false] Whether to bypass the editable check
		 * @returns {boolean} Whether the contract is editable
		 */
		isEditable(bypass = false) {
			// Now must be before expiration_date.
			if (this.contract?.expiration_date) {
				const now = new Date()
				const expirationDate = new Date(this.contract?.expiration_date)
				if (now > expirationDate) {
					return false
				}
			}
			// Now must be before editable_until.
			if (this.contract?.editable_until) {
				const now = new Date()
				const editableUntil = new Date(this.contract?.editable_until)
				if (now > editableUntil) {
					return false
				}
			}
			// We can bypass for some fields.
			if (bypass) {
				return true
			}
			return !this.sentToCustomer && !this.signedByCustomer
		},

		/**
		 * Unified method to update contract data
		 * @param {Object} contractData - The contract data to update
		 * @param {Object} options - Additional options
		 * @param {string} [options.successMessage] - Custom success message
		 * @param {string} [options.loadingMessage] - Custom loading message
		 * @param {Function} [options.onSuccess] - Callback function to run on successful update
		 * @returns {Promise<void>}
		 */
		async updateContractData(contractData, options = {}) {
			if (!this.contract?.token) return

			try {
				// Show loading message
				this.emitStatusBanner('loading', options.loadingMessage || 'Saving contract changes...')

				// Store pending changes
				this.pendingContractData = { ...contractData }

				// Clean up contractData before sending
				const cleanContractData = { ...contractData }
				if (cleanContractData.inventory_lists) {
					// Check for any items with lot count of 0
					const hasZeroLotCount = cleanContractData.inventory_lists.some(list =>
						list.items?.some(item =>
							item.description?.trim() &&
							(item.lotcount === 0 || item.lotcount === '0' || item.lotcount === '')
						)
					)

					if (hasZeroLotCount) {
						this.emitStatusBanner('error', 'Cannot save contract: All consigned properties must have a lot count greater than 0')
						return
					}

					cleanContractData.inventory_lists = cleanContractData.inventory_lists.map((list) => ({
						...list,
						items: list.items?.filter((item) => item.description?.trim() !== '') || []
					}))
				}
				if (cleanContractData.expenses) {
					// Make sure all expenses have defined an amount. If it does not, set it.
					cleanContractData.expenses = cleanContractData.expenses.map(expense => {
						if (expense.amount === undefined || expense.amount === null) {
							expense.amount = ''
						}
						return expense
					})
				}

				const response = await this.updateContract({
					token: this.contract.token,
					contractData: cleanContractData
				})

				if (response?.data?.payload) {
					this.emitStatusBanner(
						'success',
						options.successMessage || 'Contract updated successfully'
					)
					this.pendingContractData = null

					if (options.onSuccess) {
						options.onSuccess(response.data.payload)
					}
				} else {
					throw new Error('Invalid response from server')
				}
			} catch (error) {
				console.error('Contract update error:', error)

				// Handle version conflict
				if (error.name === 'ContractError' && error.type === 'MERGE_REQUIRED') {
					// If no conflicts found, auto-merge and retry
					if (!error.conflicts?.length) {
						try {
							await this.updateContract({
								token: this.contract.token,
								contractData: error.pendingChanges,
								serverContract: error.serverContract,
								forceUpdate: true
							})

							this.emitStatusBanner(
								'success',
								options.successMessage || 'Contract updated successfully'
							)
							this.pendingContractData = null

							if (options.onSuccess) {
								options.onSuccess()
							}
							return
						} catch (retryError) {
							console.error('Auto-merge failed:', retryError)
							this.emitStatusBanner(
								'error',
								retryError.response?.data?.message ||
									retryError.message ||
									'Failed to update contract. Please try again later.'
							)
							return
						}
					}

					// Show conflict modal if there are actual conflicts
					if (error.conflicts?.length > 0) {
						this.conflicts = error.conflicts
						this.pendingContractData = error.pendingChanges
						this.showConflictModal = true
						this.emitStatusBanner(
							'error',
							'Changes have been made to this contract. Please review the conflicts below.'
						)
					} else {
						this.emitStatusBanner('error', 'Failed to update contract. Please try again later.')
						this.pendingContractData = null
					}
				} else {
					this.emitStatusBanner(
						'error',
						error.response?.data?.message ||
							error.message ||
							'Failed to update contract. Please try again later.'
					)

					// Keep pending data in case of network errors
					if (!navigator.onLine) {
						EventBus.$emit(Events.CONTRACT_UNSAVED_CHANGES, true)
					} else {
						this.pendingContractData = null
					}
				}
			}
		},

		/**
		 * Helper method to emit status banner events
		 * @param {string} type - The type of status (loading, success, error)
		 * @param {string} message - The message to display
		 */
		emitStatusBanner(type, message) {
			EventBus.$emit(Events.SHOW_STATUS_BANNER, { type, message })
		},

		/**
		 * Helper method to check if a specific inventory list is editable
		 * @param {Object} list - The inventory list to check
		 * @returns {boolean} Whether the list is editable
		 */
		isInventoryListEditable(list) {
			if (!list) return false
			return !list.customer_signature || list.customer_signature.length === 0
		},

		/**
		 * Helper method to get all unsigned inventory lists
		 * @returns {Array} Array of unsigned inventory lists
		 */
		getUnsignedInventoryLists() {
			return (
				this.contract?.inventory_lists?.filter(
					(list) => !list.customer_signature || list.customer_signature.length === 0
				) || []
			)
		}
	}
}
